import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { useNavigate, Link } from 'react-router-dom';
import {
    Box,
    Button,
    TextField,
    Typography,
    CircularProgress,
    Alert,
    Container,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
} from '@mui/material';
import { authAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { registerSchema } from '../../validations/schemas';
import { useAuth } from '../../context/AuthContext';

const Register = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { login } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const registrationFormik = useFormik({
        initialValues: {
            username: '',
            name: '',
            password: '',
            confirmPassword: '',
            role: 'receptionist' // ✅ FIX: Changed from 'user' to a valid option
        },
        validationSchema: registerSchema,
        validateOnChange: true,
        validateOnBlur: true,
        onSubmit: async (values, { setSubmitting }) => {
            console.log('Form submitted with values:', values); // Debug log
            setLoading(true);
            setError('');
            try {
                // Remove confirmPassword before sending to backend
                const { confirmPassword, ...registrationData } = values;
                console.log('Sending registration data:', registrationData); // Debug log
                
                const response = await authAPI.register(registrationData);
                console.log('Registration response:', response); // Debug log

                // Registration successful - redirect to login
                toast.success(t('auth.registrationSuccess'));
                navigate('/login');
            } catch (err) {
                console.error('Registration error:', err); // Debug log
                setError(err.response?.data?.message || t('auth.registrationFailed'));
            } finally {
                setLoading(false);
                setSubmitting(false);
            }
        }
    });

    // ✅ Debug: Log validation errors
    React.useEffect(() => {
        if (Object.keys(registrationFormik.errors).length > 0) {
            console.log('Validation errors:', registrationFormik.errors);
        }
    }, [registrationFormik.errors]);

    // ✅ Manual submit handler for debugging
    const handleManualSubmit = (e) => {
        e.preventDefault();
        console.log('Manual submit triggered');
        console.log('Form values:', registrationFormik.values);
        console.log('Form errors:', registrationFormik.errors);
        console.log('Form isValid:', registrationFormik.isValid);
        console.log('Form dirty:', registrationFormik.dirty);
        
        // Force validation and submit
        registrationFormik.validateForm().then((errors) => {
            console.log('Validation result:', errors);
            if (Object.keys(errors).length === 0) {
                registrationFormik.submitForm();
            } else {
                // Touch all fields to show errors
                registrationFormik.setTouched({
                    username: true,
                    name: true,
                    password: true,
                    confirmPassword: true,
                    role: true,
                });
                toast.error(t('auth.pleaseFixErrors', 'Please fix the form errors'));
            }
        });
    };

    return (
        <Container component="main" maxWidth="xs">
            <Paper elevation={3} sx={{ p: 4, mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
                    {t('auth.registerTitle')}
                </Typography>
                {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}

                {/* ✅ Changed to use manual submit handler for better debugging */}
                <Box component="form" onSubmit={handleManualSubmit} sx={{ mt: 1, width: '100%' }}>
                    {/* Full Name */}
                    <TextField
                        margin="normal"
                        fullWidth
                        id="name"
                        name="name"
                        label={t('auth.fullName')}
                        autoComplete="name"
                        value={registrationFormik.values.name}
                        onChange={registrationFormik.handleChange}
                        onBlur={registrationFormik.handleBlur}
                        error={registrationFormik.touched.name && Boolean(registrationFormik.errors.name)}
                        helperText={registrationFormik.touched.name && registrationFormik.errors.name}
                        disabled={loading}
                    />
                    
                    {/* Username */}
                    <TextField
                        margin="normal"
                        fullWidth
                        id="username"
                        name="username"
                        label={t('auth.username')}
                        autoComplete="username"
                        autoFocus
                        value={registrationFormik.values.username}
                        onChange={registrationFormik.handleChange}
                        onBlur={registrationFormik.handleBlur}
                        error={registrationFormik.touched.username && Boolean(registrationFormik.errors.username)}
                        helperText={registrationFormik.touched.username && registrationFormik.errors.username}
                        disabled={loading}
                    />
                    
                    {/* Password */}
                    <TextField
                        margin="normal"
                        fullWidth
                        name="password"
                        label={t('auth.password')}
                        type="password"
                        id="password"
                        autoComplete="new-password"
                        value={registrationFormik.values.password}
                        onChange={registrationFormik.handleChange}
                        onBlur={registrationFormik.handleBlur}
                        error={registrationFormik.touched.password && Boolean(registrationFormik.errors.password)}
                        helperText={registrationFormik.touched.password && registrationFormik.errors.password}
                        disabled={loading}
                    />
                    
                    {/* Confirm Password */}
                    <TextField
                        margin="normal"
                        fullWidth
                        name="confirmPassword"
                        label={t('auth.confirmPassword')}
                        type="password"
                        id="confirmPassword"
                        autoComplete="new-password"
                        value={registrationFormik.values.confirmPassword}
                        onChange={registrationFormik.handleChange}
                        onBlur={registrationFormik.handleBlur}
                        error={registrationFormik.touched.confirmPassword && Boolean(registrationFormik.errors.confirmPassword)}
                        helperText={registrationFormik.touched.confirmPassword && registrationFormik.errors.confirmPassword}
                        disabled={loading}
                    />
                    
                    {/* Role Select - ✅ Added error handling */}
                    <FormControl 
                        fullWidth 
                        margin="normal"
                        error={registrationFormik.touched.role && Boolean(registrationFormik.errors.role)}
                    >
                        <InputLabel id="user-type-label">{t('auth.userType')}</InputLabel>
                        <Select
                            labelId="user-type-label"
                            id="role"
                            name="role"
                            value={registrationFormik.values.role}
                            label={t('auth.userType')}
                            onChange={registrationFormik.handleChange}
                            onBlur={registrationFormik.handleBlur}
                            disabled={loading}
                        >
                            <MenuItem value="receptionist">{t('auth.receptionist')}</MenuItem>
                            <MenuItem value="radiologist">{t('auth.radiologist')}</MenuItem>
                            <MenuItem value="representative">{t('auth.representative')}</MenuItem>
                            <MenuItem value="superAdmin">{t('auth.superAdmin')}</MenuItem>
                        </Select>
                        {registrationFormik.touched.role && registrationFormik.errors.role && (
                            <FormHelperText>{registrationFormik.errors.role}</FormHelperText>
                        )}
                    </FormControl>
                    
                    {/* Submit Button */}
                    <Button 
                        type="submit" 
                        fullWidth 
                        variant="contained" 
                        sx={{ mt: 3, mb: 2 }} 
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={24} /> : t('auth.registerButton')}
                    </Button>
                    
                    {/* Login Link */}
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                        <Typography variant="body2">
                            {t('auth.hasAccount')}{' '}
                            <Link to="/login" style={{ textDecoration: 'none', color: 'primary.main' }}>
                                {t('auth.login')}
                            </Link>
                        </Typography>
                    </Box>
                </Box>
            </Paper>
        </Container>
    );
};

export default Register;